import { useState } from "react";
import { Watch, Smartphone, Activity, Wifi, WifiOff, Battery, RefreshCw, Plus, Trash2, Settings, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface Device {
  id: string;
  device_type: string;
  device_icon: string;
  is_connected: boolean;
  last_sync_at: string;
  battery_level: number | null;
  signal_strength: string | null;
}

const AVAILABLE_DEVICES = [
  { type: "Apple Watch Series 9", icon: "⌚", category: "smartwatch" },
  { type: "Samsung Galaxy Watch 6", icon: "⌚", category: "smartwatch" },
  { type: "Fitbit Sense 2", icon: "⌚", category: "fitness" },
  { type: "Garmin Venu 3", icon: "⌚", category: "fitness" },
  { type: "Withings ScanWatch", icon: "⌚", category: "health" },
  { type: "Omron Blood Pressure Monitor", icon: "💓", category: "health" },
  { type: "Dexcom G7 CGM", icon: "📊", category: "glucose" },
  { type: "Oura Ring Gen 3", icon: "💍", category: "sleep" },
];

function DeviceCard({ 
  device, 
  onSync, 
  onDisconnect,
  isSyncing 
}: { 
  device: Device;
  onSync: (id: string) => void;
  onDisconnect: (id: string) => void;
  isSyncing: boolean;
}) {
  const batteryColor = device.battery_level 
    ? device.battery_level > 50 
      ? "text-success" 
      : device.battery_level > 20 
        ? "text-warning" 
        : "text-destructive"
    : "text-muted-foreground";

  const signalColor = device.signal_strength === "strong" 
    ? "text-success" 
    : device.signal_strength === "medium" 
      ? "text-warning" 
      : "text-destructive";

  return (
    <Card className="border-border/50 hover:border-primary/30 transition-colors">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="text-4xl">{device.device_icon}</div>
            <div className="space-y-1">
              <h3 className="font-semibold text-foreground">{device.device_type}</h3>
              <div className="flex items-center gap-2">
                {device.is_connected ? (
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30">
                    <Wifi className="h-3 w-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-muted text-muted-foreground">
                    <WifiOff className="h-3 w-3 mr-1" />
                    Disconnected
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Last synced {formatDistanceToNow(new Date(device.last_sync_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            {device.battery_level !== null && (
              <div className={`flex items-center gap-1 text-sm ${batteryColor}`}>
                <Battery className="h-4 w-4" />
                <span>{device.battery_level}%</span>
              </div>
            )}
            {device.signal_strength && (
              <div className={`flex items-center gap-1 text-xs ${signalColor}`}>
                <Wifi className="h-3 w-3" />
                <span className="capitalize">{device.signal_strength}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onSync(device.id)}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync
            </Button>
            <Button variant="ghost" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDisconnect(device.id)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AddDeviceDialog({ onAdd }: { onAdd: (device: typeof AVAILABLE_DEVICES[0]) => void }) {
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const handleAdd = () => {
    const device = AVAILABLE_DEVICES.find(d => d.type === selectedDevice);
    if (device) {
      onAdd(device);
      setIsOpen(false);
      setSelectedDevice("");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Device
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect a New Device</DialogTitle>
          <DialogDescription>
            Select a wearable or health device to connect to your account.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="device-select" className="mb-2 block">Device Type</Label>
          <Select value={selectedDevice} onValueChange={setSelectedDevice}>
            <SelectTrigger id="device-select">
              <SelectValue placeholder="Select a device..." />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_DEVICES.map((device) => (
                <SelectItem key={device.type} value={device.type}>
                  <span className="flex items-center gap-2">
                    <span>{device.icon}</span>
                    <span>{device.type}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!selectedDevice}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Connect Device
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MemberDevices() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [syncingDeviceId, setSyncingDeviceId] = useState<string | null>(null);

  // Fetch devices
  const { data: devices, isLoading } = useQuery({
    queryKey: ["member-devices", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("patient_devices")
        .select("*")
        .eq("member_id", user.id)
        .order("last_sync_at", { ascending: false });

      if (error) throw error;
      return data as Device[];
    },
    enabled: !!user?.id,
  });

  // Add device mutation
  const addDeviceMutation = useMutation({
    mutationFn: async (device: typeof AVAILABLE_DEVICES[0]) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase.from("patient_devices").insert({
        member_id: user.id,
        device_type: device.type,
        device_icon: device.icon,
        is_connected: true,
        last_sync_at: new Date().toISOString(),
        battery_level: Math.floor(Math.random() * 30) + 70,
        signal_strength: "strong",
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Device connected successfully!");
      queryClient.invalidateQueries({ queryKey: ["member-devices"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to connect device");
    },
  });

  // Sync device mutation
  const syncDeviceMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      setSyncingDeviceId(deviceId);
      
      // Simulate sync delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      const { error } = await supabase
        .from("patient_devices")
        .update({
          last_sync_at: new Date().toISOString(),
          is_connected: true,
          battery_level: Math.floor(Math.random() * 30) + 70,
        })
        .eq("id", deviceId);

      if (error) throw error;

      // Also add a new vitals reading
      if (user?.id) {
        await supabase.from("patient_vitals").insert({
          member_id: user.id,
          device_id: deviceId,
          heart_rate: Math.floor(Math.random() * 20) + 65,
          heart_rate_status: "normal",
          systolic_bp: Math.floor(Math.random() * 20) + 110,
          diastolic_bp: Math.floor(Math.random() * 15) + 70,
          bp_status: "normal",
          blood_glucose: Math.floor(Math.random() * 20) + 85,
          glucose_status: "normal",
          oxygen_saturation: Math.floor(Math.random() * 3) + 96,
          spo2_status: "normal",
          steps_count: Math.floor(Math.random() * 5000) + 6000,
          steps_goal: 10000,
          recorded_at: new Date().toISOString(),
        });
      }
    },
    onSuccess: () => {
      toast.success("Device synced! New health data available.");
      queryClient.invalidateQueries({ queryKey: ["member-devices"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-latest"] });
      queryClient.invalidateQueries({ queryKey: ["member-vitals-history"] });
      setSyncingDeviceId(null);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to sync device");
      setSyncingDeviceId(null);
    },
  });

  // Disconnect device mutation
  const disconnectDeviceMutation = useMutation({
    mutationFn: async (deviceId: string) => {
      const { error } = await supabase
        .from("patient_devices")
        .delete()
        .eq("id", deviceId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Device removed successfully");
      queryClient.invalidateQueries({ queryKey: ["member-devices"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove device");
    },
  });

  const connectedCount = devices?.filter(d => d.is_connected).length || 0;

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="font-display text-3xl font-bold text-foreground">My Devices</h1>
              <p className="mt-1 text-muted-foreground">
                Manage your connected wearables and health monitoring devices.
              </p>
            </div>
            <AddDeviceDialog onAdd={(device) => addDeviceMutation.mutate(device)} />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-8">
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-primary/10">
                    <Watch className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{devices?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Devices</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-success/10">
                    <Wifi className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{connectedCount}</p>
                    <p className="text-sm text-muted-foreground">Connected</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-warning/10">
                    <Activity className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {devices && devices.length > 0 
                        ? formatDistanceToNow(new Date(devices[0].last_sync_at), { addSuffix: false })
                        : "--"}
                    </p>
                    <p className="text-sm text-muted-foreground">Last Sync</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Device List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : devices && devices.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {devices.map((device) => (
                <DeviceCard
                  key={device.id}
                  device={device}
                  onSync={(id) => syncDeviceMutation.mutate(id)}
                  onDisconnect={(id) => disconnectDeviceMutation.mutate(id)}
                  isSyncing={syncingDeviceId === device.id}
                />
              ))}
            </div>
          ) : (
            <Card className="border-border/50">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="p-4 rounded-full bg-muted/50 mb-4">
                  <Watch className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No Devices Connected</h3>
                <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                  Connect a wearable device to start tracking your health metrics automatically.
                </p>
                <AddDeviceDialog onAdd={(device) => addDeviceMutation.mutate(device)} />
              </CardContent>
            </Card>
          )}

          {/* Compatible Devices Section */}
          <Card className="mt-8 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-primary" />
                Compatible Devices
              </CardTitle>
              <CardDescription>
                We support a wide range of wearables and health monitoring devices.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {AVAILABLE_DEVICES.map((device) => (
                  <div
                    key={device.type}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
                  >
                    <span className="text-2xl">{device.icon}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{device.type}</p>
                      <p className="text-xs text-muted-foreground capitalize">{device.category}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
